import { ThunkDispatch } from "redux-thunk";
import apis from "../../utils/apis";
import { showFeedbackModal } from "./UserFeedbackActions";
import { handleApiError } from "./utils/utils";
import { RootState } from "..";
import { AnyAction } from "redux";
import { TEMPLATE_CREATE_COMPLETED, TEMPLATE_CREATE_CONNECTED, TEMPLATE_CREATE_FAILED, TEMPLATE_CREATE_RESET, TEMPLATE_CREATE_CONNECTING } from "../types";
import { config } from "../../utils/config";
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL, formatUploadBytes } from "../../utils/uploadLimits";
import { toErrorText } from '../../utils/errorMessage';

export const fetchStructureTemplates = (params = {}) => async (dispatch: any) => {
  try {
    const response = await apis.get("/templates/structure/list", { params });
    if (response.status === 200) {
      const { templates = [], total_templates = 0, totalPages = 1 } = response.data || {};
      return { templates, total_templates, totalPages };
    } else {
      handleApiError(response, dispatch);
      return { templates: [], total_templates: 0, totalPages: 1 };
    }
  } catch (error) {
    handleApiError(error, dispatch);
    return { templates: [], total_templates: 0, totalPages: 1 };
  }
};

export const fetchBrandingTemplates = (params = {}) => async (dispatch: any) => {
  try {
    const response = await apis.get("/templates/branding/list", { params });
    if (response.status === 200) {
      const { templates = [], total_templates = 0, totalPages = 1 } = response.data || {};
      return { templates, total_templates, totalPages };
    } else {
      handleApiError(response, dispatch);
      return { templates: [], total_templates: 0, totalPages: 1 };
    }
  } catch (error) {
    handleApiError(error, dispatch);
    return { templates: [], total_templates: 0, totalPages: 1 };
  }
};

export const createStructureTemplate =
  (data: any, files?: { [key: string]: File }) =>
  async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    dispatch({ type: TEMPLATE_CREATE_RESET });
    dispatch({ type: TEMPLATE_CREATE_CONNECTING });

    try {
      const fd = new FormData();
      Object.entries<string | File | any>(data || {}).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (typeof v === "object") {
          fd.append(k, JSON.stringify(v));
        } else {
          fd.append(k, String(v));
        }
      });

      // A structure template IS one document, so there is nothing to batch:
      // this endpoint takes a single file and the request is as small as it
      // can be made. The only thing the client can do is refuse a file that
      // would be reset at the edge, with a sentence saying why — see the note
      // in `utils/uploadLimits`. A single file over the EU edge's own
      // request-body limit will still fail until that limit is raised.
      if (files?.document) {
        if (files.document.size > MAX_UPLOAD_BYTES) {
          throw new Error(
            `${files.document.name} is ${formatUploadBytes(files.document.size)}. The limit is ${MAX_UPLOAD_LABEL} per document.`
          );
        }
        fd.append("document", files.document, files.document.name);
      }

      /*
        NOTHING PRE-DRAWN. This used to dispatch five invented steps —
        Uploading / Validating / Extracting / Analyzing / Generating — before a
        single word had come back from the engine, and the real `step_started`
        events then overwrote whichever of them happened to match by name.

        Two things were wrong with that. The list was a GUESS at a pipeline
        this code does not own: a stage the engine renamed or dropped sat at
        "pending" forever, and a stage it added never appeared at all. And it
        showed four steps as work still to come before anything had been
        confirmed as started, which reads as knowledge nobody had.

        The engine emits `step_started` / `step_progress` / `step_completed`
        for this pipeline already (see `StructureForm`), so the stage rail
        builds itself from those. Until the first one lands the panel shows a
        connecting sliver, which is the honest picture of "we have asked".
      */
      dispatch({ type: TEMPLATE_CREATE_CONNECTED });

      // Single REST call — creates record, uploads file, queues AI processing
      const resp = await apis({
        method: "POST",
        url: "/templates/structure",
        data: fd,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (resp.status !== 201) {
        throw new Error(toErrorText(resp.data?.error) || "Failed to create template");
      }

      const { template, processing, pubsub } = resp.data;
      const templateUuid = template?.id || processing?.templateUuid;
      const correlationId = processing?.correlationId;

      // Return info for PubSub connection (StructureForm handles live progress)
      return {
        status: 201,
        data: {
          uuid: templateUuid,
          correlationId,
          pubsub,
          template,
        },
      };
    } catch (err: any) {
      dispatch({ type: TEMPLATE_CREATE_FAILED, payload: { error: String(err) } });
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: err.message || "Template creation failed.",
          modalType: "error",
          duration: 3500,
        })
      );
      throw err;
    }
  };

export const resetTemplateCreation = () => (dispatch: any) => {
  dispatch({ type: TEMPLATE_CREATE_RESET });
};

/**
 * Get the status of a template that may still be processing.
 * Returns the template data including its current status.
 */
export const checkTemplateStatus = (templateId: string) => async (dispatch: any) => {
  try {
    const response = await apis.get(`/templates/structure/${templateId}`);
    if (response.status === 200) {
      return response.data;
    } else {
      handleApiError(response, dispatch);
      return null;
    }
  } catch (error) {
    handleApiError(error, dispatch);
    return null;
  }
};

export const getStructureTemplate = (templateId: any) => async (dispatch: any) => {
  try {
    const response = await apis.get(`/templates/structure/${templateId}`);
    if (response.status === 200) {
      return response.data;
    } else {
      handleApiError(response, dispatch);
      return null;
    }
  } catch (error) {
    handleApiError(error, dispatch);
    return null;
  }
};

export const updateStructureTemplate = (templateId: any, data: any, files?: { [key: string]: File }) => async (dispatch: any) => {
  try {
    let response;
    if (files && Object.keys(files).length > 0) {
      const formData = new FormData();
      Object.keys(data).forEach(key => formData.append(key, typeof data[key] === 'object' ? JSON.stringify(data[key]) : data[key]));
      Object.keys(files).forEach(key => formData.append(key, files[key]));
      response = await apis.put(`/templates/structure/${templateId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } else {
      response = await apis.put(`/templates/structure/${templateId}`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
    }
    if (response.status === 200) {
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: 'Structure template updated',
        showModal: true,
        duration: 3000
      }));
      return response.data;
    } else {
      handleApiError(response, dispatch);
    }
  } catch (error) {
    handleApiError(error, dispatch);
    throw error;
  }
};

export const deleteStructureTemplate = (templateId: any) => async (dispatch: any) => {
  try {
    const response = await apis.delete(`/templates/structure/${templateId}`);
    if (response.status === 200) {
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: 'Structure template deleted',
        showModal: true,
        duration: 3000
      }));
      return response.data;
    } else {
      handleApiError(response, dispatch);
    }
  } catch (error) {
    handleApiError(error, dispatch);
    throw error;
  }
};

export const deleteStructureTemplatesBulk = (templateIds: string[]) => async (dispatch: any) => {
  try {
    const results = await Promise.allSettled(
      templateIds.map(id => apis.delete(`/templates/structure/${id}`))
    );
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    dispatch(showFeedbackModal({
      modalType: failed > 0 ? 'warning' : 'success',
      message: failed > 0
        ? `Deleted ${succeeded} template(s), ${failed} failed`
        : `${succeeded} template(s) deleted`,
      showModal: true,
      duration: 3000
    }));
    return { succeeded, failed };
  } catch (error) {
    handleApiError(error, dispatch);
    throw error;
  }
};

export const reanalyzeStructureTemplate = (templateId: string) => async (dispatch: any) => {
  try {
    dispatch({ type: TEMPLATE_CREATE_RESET });
    /* Same pipeline, same rule: the engine names its own stages — see the
       note in `createStructureTemplate`. */
    dispatch({ type: TEMPLATE_CREATE_CONNECTING });

    const response = await apis.post(`/templates/structure/${templateId}/reanalyze`);
    if (response.status === 200) {
      dispatch({ type: TEMPLATE_CREATE_CONNECTED });
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: 'Re-analysis queued',
        showModal: true,
        duration: 3000
      }));
      return response.data;
    } else {
      dispatch({ type: TEMPLATE_CREATE_FAILED, payload: { error: 'Failed to queue re-analysis' } });
      handleApiError(response, dispatch);
      return null;
    }
  } catch (error) {
    dispatch({ type: TEMPLATE_CREATE_FAILED, payload: { error: String(error) } });
    handleApiError(error, dispatch);
    return null;
  }
};

export const createBrandingTemplate = (templateId: string | undefined, data: any, files: any) => async (dispatch: any) => {
  try {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, typeof data[key] === 'object' ? JSON.stringify(data[key]) : data[key]));
    Object.keys(files).forEach(key => formData.append(key, files[key]));
    const response = await apis.post("/templates/branding", formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (response.status === 201) {
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: 'Branding template created',
        showModal: true,
        duration: 3000
      }));
      return response.data;
    } else {
      handleApiError(response, dispatch);
    }
  } catch (error) {
    handleApiError(error, dispatch);
    throw error;
  }
};

export const extractBrandingFromDocument = (file: File) => async (dispatch: any) => {
  try {
    const formData = new FormData();
    formData.append('document', file);
    const response = await apis.post('/templates/branding/extract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (response.status === 200 && response.data?.extracted) {
      return {
        success: true,
        branding: response.data.branding,
        confidence: response.data.confidence,
        filename: response.data.filename,
        html_preview: response.data.html_preview || '',
        original_file_ref: response.data.original_file_ref || null,
        message: response.data.message
      };
    } else {
      handleApiError(response, dispatch);
      return { success: false, error: 'Failed to extract branding' };
    }
  } catch (error: any) {
    handleApiError(error, dispatch);
    return { success: false, error: error?.message || 'Failed to extract branding' };
  }
};

export const getBrandingTemplate = (templateId: any) => async (dispatch: any) => {
  try {
    const response = await apis.get(`/templates/branding/${templateId}`);
    if (response.status === 200) {
      return response.data;
    } else {
      handleApiError(response, dispatch);
      return null;
    }
  } catch (error) {
    handleApiError(error, dispatch);
    return null;
  }
};

export const updateBrandingTemplate = (templateId: any, data: any, files: any) => async (dispatch: any) => {
  try {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, typeof data[key] === 'object' ? JSON.stringify(data[key]) : data[key]));
    Object.keys(files).forEach(key => formData.append(key, files[key]));
    const response = await apis.put(`/templates/branding/${templateId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (response.status === 200) {
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: 'Branding template updated',
        showModal: true,
        duration: 3000
      }));
      return response.data;
    } else {
      handleApiError(response, dispatch);
    }
  } catch (error) {
    handleApiError(error, dispatch);
    throw error;
  }
};

export const deleteBrandingTemplate = (templateId: any) => async (dispatch: any) => {
  try {
    const response = await apis.delete(`/templates/branding/${templateId}`);
    if (response.status === 200) {
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: 'Branding template deleted',
        showModal: true,
        duration: 3000
      }));
      return response.data;
    } else {
      handleApiError(response, dispatch);
    }
  } catch (error) {
    handleApiError(error, dispatch);
    throw error;
  }
};

export const shareTemplate = (data: any) => async (dispatch: any) => {
  try {
    const response = await apis.post("/templates/share", data);
    if (response.status === 200) {
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: 'Template shared',
        showModal: true,
        duration: 3000
      }));
      return response.data;
    } else {
      handleApiError(response, dispatch);
    }
  } catch (error) {
    handleApiError(error, dispatch);
    throw error;
  }
};

/**
 * Get the URL to fetch the template file (for preview/download)
 */
export const getBrandingTemplateFileUrl = (templateId: string): string => {
  return `${config.baseUrl}/templates/branding/${templateId}/file`;
};
